import { useIsMutating } from "@tanstack/react-query";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogProps,
} from "../../../components/CommonComponents/Dialog/Dialog";
import Input from "../../../components/CommonComponents/Input/Input";
import { Menu } from "../../../components/CommonComponents/Menu/Menu";
import Select from "../../../components/CommonComponents/Select/Select";
import Switch from "../../../components/CommonComponents/Switch/Switch";
import {
  TRegistrationField,
  usePatchRegistrationField,
} from "../../../hooks/registration-field-hooks";

type OnSubmit = (_: {
  label?: string;
  placeholder?: string;
  active?: boolean;
  mandatory?: boolean;
  options?: number[];
  format?: string;
}) => unknown;

function EditForSelectables({
  field,
  onSubmit,
}: {
  field: TRegistrationField;
  onSubmit: OnSubmit;
}) {
  const isUpdating = useIsMutating() > 0;
  const anchor = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const options = field?.options || [];

  const [enabledOptions, setEnabledOptions] = useState<number[]>([]);

  useEffect(() => {
    setEnabledOptions((p) =>
      (options || []).filter((e) => e?.active).map((e) => e.id)
    );
  }, [options]);

  const { register, formState, handleSubmit } = useForm({
    defaultValues: {
      label_name: field.label_name,
      placeholder_name: field.placeholder_name || "",
      active: field?.active || false,
      mandatory: field?.mandatory || false,
    },
  });

  const toggleOption = (id: number) =>
    setEnabledOptions((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
    );

  return (
    <form
      noValidate
      onSubmit={handleSubmit((data) =>
        onSubmit({ ...data, options: enabledOptions })
      )}
      className="w-full md:w-[650px] grid grid-cols-1 gap-6 p-6"
    >
      <div className="col-span-1 md:col-span-2">
        <Input
          containerClass="w-full"
          placeholder="Label Name"
          label="Enter Label Name"
          {...register("label_name", {
            required: "* Required",
            maxLength: {
              message: "Cannot have more than 200 characters",
              value: 200,
            },
          })}
          error={!!formState.errors?.label_name}
          helperText={formState?.errors?.label_name?.message?.toString()}
        />
      </div>
      <div className="col-span-1">
        <Select
          containerClass="w-full"
          value={field.label_name}
          containerRef={anchor}
          label="Select Options"
          onClick={(e) => setOpen(true)}
          onMouseDown={(e) => e.preventDefault()}
        >
          <option value={field.label_name}>{field.label_name}</option>
        </Select>
      </div>
      <div className="col-span-1 flex flex-wrap md:flex-nowrap gap-4 justify-start md:justify-evenly items-center">
        <div className="flex space-x-4 basis-full md:basis-auto justify-start md:justify-between">
          <label htmlFor="mandatory" className="font-semibold">
            Mandatory
          </label>
          <Switch id="mandatory" {...register("mandatory")} />
        </div>
        <div className="flex space-x-4 basis-full md:basis-auto justify-start md:justify-between">
          <label htmlFor="active" className="font-semibold">
            Field Activate
          </label>
          <Switch id="active" {...register("active")} />
        </div>
      </div>
      <div className="col-span-1 md:col-span-2  flex items-center justify-center">
        <button
          type="submit"
          disabled={isUpdating}
          className="bg-primary px-6 py-2 rounded-full font-bold text-white"
        >
          Update
        </button>
      </div>
      <Menu anchor={anchor.current} open={open} onClose={() => setOpen(false)}>
        <div className="h-32 lg:h-48 overflow-auto space-y-2 p-4">
          {(options || []).map((e) => (
            <div className="flex justify-between">
              <p className="text-grey-600">{e.name}</p>
              <Switch
                checked={enabledOptions.includes(e.id)}
                onChange={() => toggleOption(e.id)}
              />
            </div>
          ))}
        </div>
      </Menu>
    </form>
  );
}

export default function EditRegistrationFieldDialog({
  field,
  onSave,
  ...dialogProps
}: {
  field: TRegistrationField;
  onSave: () => any;
} & Omit<DialogProps, "className">) {
  const patchField = usePatchRegistrationField();

  let toRender: ReactNode = null;

  const onSubmit: OnSubmit = (data) => {
    patchField.mutate(
      { registration_field_id: field.registration_field_id, ...data },
      {
        onSuccess: () => {
          onSave();
          dialogProps.onClose();
        },
      }
    );
  };

  switch (field.field_type) {
    case "select":
      toRender = <EditForSelectables field={field} onSubmit={onSubmit} />;
      break;

    default:
      dialogProps.onClose();
      toRender = null;
  }

  return (
    <Dialog {...dialogProps}>
      <div className="p-4 flex justify-center items-center">
        <h4 className="m-0 grow text-center text-2xl font-bold mt-4">
          Edit Field
        </h4>
        <button className="text-xl" onClick={dialogProps.onClose}>
          <i className="fa fa-close"></i>
        </button>
      </div>
      <hr className="p-2 mx-4" />
      {toRender}
    </Dialog>
  );
}
